import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { refreshTokenSucess } from 'store/auth';
import { updateLanguagePreference } from 'services/users-service';

const LANGUAGES = [
  { code: 'pt', label: 'PT-BR' },
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
];

const Wrapper = styled.div`
  position: relative;
`

const LangButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid var(--vo-line);
  background: white;
  cursor: pointer;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-slate);
  white-space: nowrap;

  &:hover {
    background: var(--vo-mist);
  }
`

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 200ms ease;
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0deg)'};
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 200px;
  background: white;
  border-radius: 14px;
  box-shadow: 0 8px 24px color-mix(in srgb, var(--vo-escuro) 12%, transparent);
  z-index: 1000;
  overflow: hidden;
  display: ${({ $open }) => $open ? 'block' : 'none'};
`

const DropdownHeader = styled.div`
  padding: 12px 16px 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: var(--vo-steel);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const LangOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: ${({ $active }) => $active ? 'var(--vo-mist)' : 'white'};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? 600 : 400};
  color: ${({ $active }) => $active ? 'var(--vo-ink)' : 'var(--vo-ink-2)'};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--vo-mist);
  }
`

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-brand)' }}>
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)

const GlobeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
)

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
)

const LanguageSelector = () => {
  const { i18n, t } = useTranslation('common');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleChange = async (code) => {
    setOpen(false);
    if (code === i18n.language) return;

    i18n.changeLanguage(code);
    queryClient.invalidateQueries();

    try {
      const response = await updateLanguagePreference(code);
      if (response.data?.token) {
        dispatch(refreshTokenSucess(response.data));
      }
    } catch (error) {
      console.warn('Language preference save failed:', error.message);
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <LangButton onClick={() => setOpen(v => !v)}>
        <GlobeIcon />
        {currentLang.label}
        <ChevronIcon $open={open}>
          <ChevronDownIcon />
        </ChevronIcon>
      </LangButton>
      <Dropdown $open={open}>
        <DropdownHeader>{t('header.language')}</DropdownHeader>
        {LANGUAGES.map(({ code, label }) => (
          <LangOption
            key={code}
            $active={code === i18n.language}
            onClick={() => handleChange(code)}
          >
            {label}
            {code === i18n.language && <CheckIcon />}
          </LangOption>
        ))}
      </Dropdown>
    </Wrapper>
  );
};

export default LanguageSelector;
